/**
 * Handler for a REAL CMMD REST authentication failure (HTTP 401 from the CMMD
 * proxy). Wired as the default `cmmdApiClient` / `createCmmdApiClient`
 * `onAuthExpired`, so it fires for ANY CMMD REST surface.
 *
 * Product rule: Forge is a CMMD product. A dead CMMD data-plane token means the
 * product session is unusable. Soft "reconnect" while keeping a paired Forge
 * cookie strands users on authenticated routes that bounce `/login` home, or on
 * fatal error screens. On 401 we logout the Forge HTTP session and hard-navigate
 * to `/login` so **Continue with CMMD** can remint tokens via OAuth.
 *
 * A 403 is an authorization denial on a still-valid session — do nothing.
 *
 * Single-flight is the promise guard below: it coalesces concurrent 401s onto
 * one redirect and is what actually prevents the loop. The connection
 * supervisor's re-auth admission (`beginCloudReAuthRedirect`) rides along as
 * defense in depth in the same window; it is not an independent guarantee.
 * Clearing the failure (exempt path or successful renew) resets the episode
 * and allows a fresh redirect if a genuinely new 401 arrives later.
 */

import { redirectToForgeLoginAfterAuthExpired } from "~/lib/authExpiredRedirect";
import { beginCloudReAuthRedirect, endCloudReAuthRedirect } from "~/rpc/wsConnectionState";

let redirectInFlight: Promise<void> | null = null;

export async function handleCmmdAuthExpired(status?: number): Promise<void> {
  // Only a 401 (CMMD token expired / invalid) is actionable. A 403 is an
  // authorization denial on a valid session — do nothing.
  if (status !== undefined && status !== 401) return;
  // Node/SSR unit tests and prerender have no navigation surface.
  if (typeof window === "undefined") return;
  if (redirectInFlight) return redirectInFlight;
  if (!beginCloudReAuthRedirect()) return undefined;
  redirectInFlight = redirectToForgeLoginAfterAuthExpired().finally(() => {
    endCloudReAuthRedirect();
    redirectInFlight = null;
  });
  return redirectInFlight;
}

/** Test-only: clear the in-flight redirect guard between cases. */
export function __resetCmmdAuthRedirectForTests(): void {
  endCloudReAuthRedirect();
  redirectInFlight = null;
}
